import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/contexts/ToastContext";
import {
  registrationApi,
  type RegistrationConfig,
  type FormFieldConfig,
  type RegisterPatronData,
} from "@/lib/api/registration";
import { patronTypesApi, type PatronType } from "@/lib/api/patron-types";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  Upload,
  Info,
} from "lucide-react";
import { RegistrationFileDropzone } from "@/components/patron/RegistrationFileDropzone";
import AuthLayout from "@/components/layout/AuthLayout";
import { RegistrationSuccessConfetti } from "@/components/patron/RegistrationSuccessConfetti";
import {
  REGISTRATION_DOCUMENT_MAX_LABEL,
  REGISTRATION_PROFILE_PHOTO_MAX_LABEL,
  registrationFileSizeError,
} from "@/lib/registration/file-limits";
import {
  formatRegistrationFailure,
  formatRegistrationRequestError,
} from "@/lib/registration/registration-error";
import { registrationSuccessView } from "@/lib/registration/success-copy";

function getPasswordStrength(pw: string): number {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  return score;
}

const strengthLabels = ["Weak", "Fair", "Good", "Strong"];
const strengthColors = ["bg-red-500", "bg-amber-500", "bg-blue-500", "bg-green-500"];

const STEPS = [
  { key: "type", label: "Account type" },
  { key: "details", label: "Personal details" },
  { key: "password", label: "Password" },
  { key: "additional", label: "Finish" },
] as const;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantSlug = searchParams.get("tenantSlug") || undefined;
  const toast = useToast();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState<RegistrationConfig | null>(null);
  const [patronTypes, setPatronTypes] = useState<PatronType[]>([]);
  const [selectedPatronType, setSelectedPatronType] = useState<string>("");
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [documentFiles, setDocumentFiles] = useState<Record<string, File>>({});
  const [fieldFiles, setFieldFiles] = useState<Record<string, File>>({});
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [branches, setBranches] = useState<
    { id: string; name: string; code: string; isMain: boolean }[]
  >([]);
  const [selectedHomeBranch, setSelectedHomeBranch] = useState<string>("");

  useEffect(() => {
    loadConfig();
  }, [tenantSlug]);

  useEffect(() => {
    if (selectedPatronType) loadPatronTypeConfig();
  }, [selectedPatronType, tenantSlug]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const [configData, patronTypesData] = await Promise.all([
        registrationApi.getRegistrationConfig(undefined, tenantSlug),
        patronTypesApi.getPublic(tenantSlug),
      ]);
      if (!configData.allowSelfRegistration) {
        setMessage(
          "This library does not allow self-registration. Please contact the library administration."
        );
        return;
      }
      setConfig(configData);
      setPatronTypes(patronTypesData);
      if (configData.showBranchPreference) {
        try {
          const branchData = await registrationApi.getPublicBranches(tenantSlug);
          setBranches(branchData);
        } catch {
          /* non-critical */
        }
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setMessage(
          error.response?.data?.message ||
            "Library not found. Please verify the domain is correct or contact support."
        );
      } else if (error.response?.status === 403) {
        setMessage("Access denied. Please contact the library administration.");
      } else {
        setMessage(
          error.response?.data?.message ||
            "Failed to load registration form. Please try again later."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPatronTypeConfig = async () => {
    try {
      const patronTypeConfig = await registrationApi.getRegistrationConfig(
        selectedPatronType,
        tenantSlug
      );
      setConfig(patronTypeConfig);
    } catch (error) {
      console.error("Failed to load patron type config:", error);
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData({ ...formData, [fieldName]: value });
    if (errors[fieldName]) {
      const newErrors = { ...errors };
      delete newErrors[fieldName];
      setErrors(newErrors);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const kind = fieldName === "profilePicture" ? "photo" : "document";
    const sizeError = registrationFileSizeError(file, kind);
    if (sizeError) {
      e.target.value = "";
      if (fieldName === "profilePicture") setProfilePicture(null);
      setErrors((prev) => ({ ...prev, [fieldName]: sizeError }));
      return;
    }
    if (fieldName === "profilePicture") setProfilePicture(file);
    else setFiles([...files, file]);
    setErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  // --- Per-step validation ---
  const validateStep = (s: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (s === 0) {
      if (!selectedPatronType) newErrors.patronType = "Please select a patron type";
    }

    if (s === 1) {
      if (!formData.firstName?.trim()) newErrors.firstName = "First name is required";
      if (!formData.lastName?.trim()) newErrors.lastName = "Last name is required";
      if (!formData.email) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!formData.phone?.trim()) newErrors.phone = "Phone number is required";
    }

    if (s === 2) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    if (s === 3 && config) {
      for (const field of config.formFields) {
        if (field.type === "file") {
          const uploaded = fieldFiles[field.name];
          if (field.required && !uploaded) {
            newErrors[field.name] = `${field.label} is required`;
          } else if (uploaded) {
            const sizeError = registrationFileSizeError(uploaded, "document");
            if (sizeError) newErrors[field.name] = sizeError;
          }
          continue;
        }
        if (field.required && !formData[field.name]) {
          newErrors[field.name] = `${field.label} is required`;
        }
        if (
          field.type === "email" &&
          formData[field.name] &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field.name])
        ) {
          newErrors[field.name] = `${field.label} must be a valid email`;
        }
        if (field.type === "number" && formData[field.name]) {
          const num = Number(formData[field.name]);
          if (isNaN(num)) newErrors[field.name] = `${field.label} must be a number`;
          else {
            if (field.validation?.min !== undefined && num < field.validation.min)
              newErrors[field.name] =
                field.validation.customMessage ||
                `${field.label} must be at least ${field.validation.min}`;
            if (field.validation?.max !== undefined && num > field.validation.max)
              newErrors[field.name] =
                field.validation.customMessage ||
                `${field.label} must be at most ${field.validation.max}`;
          }
        }
        if ((field.type === "text" || field.type === "textarea") && formData[field.name]) {
          const value = String(formData[field.name]);
          if (field.validation?.minLength && value.length < field.validation.minLength)
            newErrors[field.name] =
              field.validation.customMessage ||
              `${field.label} must be at least ${field.validation.minLength} characters`;
          if (field.validation?.maxLength && value.length > field.validation.maxLength)
            newErrors[field.name] =
              field.validation.customMessage ||
              `${field.label} must be at most ${field.validation.maxLength} characters`;
          if (field.validation?.pattern && !new RegExp(field.validation.pattern).test(value))
            newErrors[field.name] =
              field.validation.customMessage || `${field.label} format is invalid`;
        }
      }
      if (config.requireProfilePicture && !profilePicture)
        newErrors.profilePicture = "Profile picture is required";
      if (profilePicture) {
        const sizeError = registrationFileSizeError(profilePicture, "photo");
        if (sizeError) newErrors.profilePicture = sizeError;
      }
      for (const [docType, file] of Object.entries(documentFiles)) {
        const sizeError = registrationFileSizeError(file, "document");
        if (sizeError) newErrors[`document:${docType}`] = sizeError;
      }
      if (config.requireDocuments && config.requireDocuments.length > 0) {
        const missing = config.requireDocuments.filter((d) => !documentFiles[d]);
        if (missing.length > 0) newErrors.documents = `Please upload: ${missing.join(", ")}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!validateStep(step) || !config || !selectedPatronType) return;

    try {
      setSubmitting(true);
      setSubmitError("");
      const registrationData: RegisterPatronData = {
        patronTypeId: selectedPatronType,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        formData: Object.fromEntries(
          Object.entries(formData).filter(
            ([key]) =>
              !["email", "password", "confirmPassword", "firstName", "lastName", "phone"].includes(
                key
              )
          )
        ),
        ...(selectedHomeBranch && { homeBranchId: selectedHomeBranch }),
      };
      const filesToUpload: File[] = [];
      if (profilePicture) filesToUpload.push(profilePicture);
      const documentTypes: Record<string, string> = {};
      if (config.requireDocuments && config.requireDocuments.length > 0) {
        config.requireDocuments.forEach((docType) => {
          if (documentFiles[docType]) {
            filesToUpload.push(documentFiles[docType]);
            documentTypes[docType] = documentFiles[docType].name;
          }
        });
      }
      for (const field of config.formFields) {
        if (field.type !== "file") continue;
        const file = fieldFiles[field.name];
        if (!file) continue;
        filesToUpload.push(file);
        documentTypes[field.label] = file.name;
      }
      registrationData.documentTypes = documentTypes;
      const result = await registrationApi.submitRegistration(
        registrationData,
        filesToUpload,
        tenantSlug
      );
      if (result.success) {
        setSuccess(true);
        setMessage(result.message);
      } else {
        const failure = formatRegistrationFailure(
          result.message ? { message: result.message } : undefined
        );
        setSubmitError(failure);
        toast.error(failure);
      }
    } catch (error: unknown) {
      const failure = formatRegistrationRequestError(error);
      setSubmitError(failure);
      toast.error(failure);
    } finally {
      setSubmitting(false);
    }
  };

  const evaluateCondition = (conditional: FormFieldConfig["conditional"]): boolean => {
    if (!conditional) return true;
    const fieldValue = formData[conditional.field];
    switch (conditional.operator) {
      case "equals":
        return fieldValue === conditional.value;
      case "notEquals":
        return fieldValue !== conditional.value;
      case "contains":
        return String(fieldValue || "").includes(String(conditional.value));
      default:
        return true;
    }
  };

  const renderField = (field: FormFieldConfig) => {
    const value = formData[field.name] || "";
    const error = errors[field.name];
    const shouldShow = !field.conditional || evaluateCondition(field.conditional);
    if (!shouldShow) return null;
    const errorClass = error
      ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
      : "";
    return (
      <div key={field.id} className="space-y-1.5">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        {field.type === "text" && (
          <Input
            id={field.name}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={errorClass}
            disabled={submitting}
          />
        )}
        {field.type === "email" && (
          <Input
            id={field.name}
            type="email"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={errorClass}
            disabled={submitting}
          />
        )}
        {field.type === "number" && (
          <Input
            id={field.name}
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={errorClass}
            disabled={submitting}
          />
        )}
        {field.type === "textarea" && (
          <textarea
            id={field.name}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            disabled={submitting}
            className={`flex w-full min-h-[100px] px-3 py-2 text-sm border border-input rounded-lg bg-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 ${errorClass}`}
          />
        )}
        {field.type === "select" && field.options && (
          <Select value={value} onValueChange={(val) => handleFieldChange(field.name, val)}>
            <SelectTrigger className={errorClass}>
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {field.type === "date" && (
          <Input
            id={field.name}
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className={errorClass}
            disabled={submitting}
          />
        )}
        {field.type === "checkbox" && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.name}
              checked={value === true}
              onCheckedChange={(checked) => handleFieldChange(field.name, checked)}
            />
            <Label htmlFor={field.name} className="font-normal">
              {field.placeholder || "I agree"}
            </Label>
          </div>
        )}
        {field.type === "file" && (
          <RegistrationFileDropzone
            id={field.name}
            label={field.label}
            fileName={fieldFiles[field.name]?.name}
            hasError={Boolean(error)}
            disabled={submitting}
            onReject={(message) => {
              setFieldFiles((prev) => {
                const next = { ...prev };
                delete next[field.name];
                return next;
              });
              setFormData((prev) => {
                const next = { ...prev };
                delete next[field.name];
                return next;
              });
              setErrors((prev) => ({ ...prev, [field.name]: message }));
            }}
            onSelect={(file) => {
              setFieldFiles((prev) => ({ ...prev, [field.name]: file }));
              setFormData((prev) => ({ ...prev, [field.name]: file.name }));
              setErrors((prev) => {
                if (!prev[field.name]) return prev;
                const next = { ...prev };
                delete next[field.name];
                return next;
              });
            }}
            onClear={() => {
              setFieldFiles((prev) => {
                const next = { ...prev };
                delete next[field.name];
                return next;
              });
              handleFieldChange(field.name, "");
            }}
          />
        )}
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  };

  // Check if step 4 has any content
  const hasAdditionalStep =
    config &&
    (config.formFields.length > 0 ||
      config.requireProfilePicture ||
      (config.requireDocuments && config.requireDocuments.length > 0) ||
      (config.showBranchPreference && branches.length > 0));

  const totalSteps = hasAdditionalStep ? 4 : 3;
  const activeSteps = STEPS.slice(0, totalSteps);
  const isLastStep = step === totalSteps - 1;

  // ========================
  //  Loading / Error / Success states
  // ========================

  if (loading) {
    return (
      <AuthLayout
        headline="Join your library community."
        subtext="Create your account to access catalog search, borrowing, and personalized recommendations."
      >
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-sm text-slate-500">Loading registration form...</p>
        </div>
      </AuthLayout>
    );
  }

  if (!config || !config.allowSelfRegistration) {
    return (
      <AuthLayout
        headline="Join your library community."
        subtext="Create your account to access catalog search, borrowing, and personalized recommendations."
      >
        <div className="text-center space-y-5 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 ring-8 ring-red-50/50">
            <XCircle className="h-9 w-9 text-red-600" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Registration not available
            </h2>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              {message || "This library does not allow self-registration."}
            </p>
          </div>
          <Button onClick={() => navigate("/")} className="w-full" size="lg">
            Go to home
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    const successView = registrationSuccessView(message);
    return (
      <AuthLayout
        headline={successView.showVerificationPrompt ? "Welcome aboard!" : "Application received"}
        subtext={
          successView.showVerificationPrompt
            ? "Your registration has been submitted successfully."
            : "The library will review this application."
        }
      >
        <RegistrationSuccessConfetti />
        <div className="text-center space-y-5 animate-fade-in-up relative z-[1]">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 ring-8 ring-green-50/50">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">{successView.title}</h2>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{message}</p>
          </div>
          {successView.showVerificationPrompt ? (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-primary text-left">
                  Check your email for a verification link to activate your account.
                </p>
              </div>
            </div>
          ) : null}
          <Button onClick={() => navigate("/login")} className="w-full" size="lg">
            Go to sign in
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // ========================
  //  Multi-step form
  // ========================

  const passwordValue = formData.password || "";
  const strength = passwordValue ? getPasswordStrength(passwordValue) : 0;

  const stepTitles = [
    { title: "Choose your account type", desc: "Select the patron type that best describes you." },
    { title: "Personal details", desc: "Tell us a bit about yourself." },
    { title: "Create your password", desc: "Choose a strong password to secure your account." },
    { title: "Almost done", desc: "Fill in any remaining details and submit your registration." },
  ];

  return (
    <AuthLayout
      headline="Join your library community."
      subtext="Create your account to access catalog search, borrowing, and personalized recommendations."
      maxWidth="lg"
      verticalAlign="top"
    >
      <div className="space-y-6 animate-fade-in-up">
        {/* Step indicator */}
        <div>
          <p className="text-xs text-slate-400 mb-3">
            Step {step + 1} of {totalSteps}
          </p>
          <div className="flex items-center gap-1">
            {activeSteps.map((s, i) => (
              <div key={s.key} className="flex items-center flex-1">
                <button
                  type="button"
                  onClick={() => {
                    if (i < step) setStep(i);
                  }}
                  disabled={i > step}
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-colors shrink-0 ${
                    i === step
                      ? "bg-primary text-white ring-4 ring-primary/20"
                      : i < step
                        ? "bg-primary text-white cursor-pointer hover:bg-primary-600"
                        : "bg-slate-100 text-slate-400"
                  }`}
                  aria-label={`Step ${i + 1}: ${s.label}`}
                >
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </button>
                {i < activeSteps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-1.5 rounded-full ${i < step ? "bg-primary" : "bg-slate-200"}`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex mt-1.5">
            {activeSteps.map((s, i) => (
              <p
                key={s.key}
                className={`flex-1 text-[10px] ${i === step ? "text-primary font-medium" : "text-slate-400"}`}
              >
                {s.label}
              </p>
            ))}
          </div>
        </div>

        {/* Page header */}
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">
            {stepTitles[step].title}
          </h2>
          <p className="mt-1 text-sm text-slate-500">{stepTitles[step].desc}</p>
        </div>

        {/* ============ STEP 0: Account type ============ */}
        {step === 0 && (
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="patronType">
                Patron type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedPatronType}
                onValueChange={(val) => {
                  setSelectedPatronType(val);
                  if (errors.patronType) {
                    const e = { ...errors };
                    delete e.patronType;
                    setErrors(e);
                  }
                }}
              >
                <SelectTrigger
                  id="patronType"
                  className={
                    errors.patronType
                      ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                      : ""
                  }
                >
                  <SelectValue placeholder="Select your patron type" />
                </SelectTrigger>
                <SelectContent>
                  {patronTypes.map((pt) => (
                    <SelectItem key={pt.id} value={pt.id}>
                      {pt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.patronType && (
                <p className="text-sm text-red-600" role="alert">
                  {errors.patronType}
                </p>
              )}
              <p className="text-xs text-slate-500">
                Your patron type determines which services and resources are available to you.
              </p>
            </div>
          </div>
        )}

        {/* ============ STEP 1: Personal details ============ */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">
                  First name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName || ""}
                  onChange={(e) => handleFieldChange("firstName", e.target.value)}
                  placeholder="John"
                  disabled={submitting}
                  aria-invalid={!!errors.firstName}
                  className={
                    errors.firstName
                      ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                      : ""
                  }
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600" role="alert">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">
                  Last name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName || ""}
                  onChange={(e) => handleFieldChange("lastName", e.target.value)}
                  placeholder="Doe"
                  disabled={submitting}
                  aria-invalid={!!errors.lastName}
                  className={
                    errors.lastName
                      ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                      : ""
                  }
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600" role="alert">
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">
                Email address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                placeholder="you@example.com"
                disabled={submitting}
                aria-invalid={!!errors.email}
                className={
                  errors.email
                    ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                    : ""
                }
              />
              {errors.email && (
                <p className="text-sm text-red-600" role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone">
                Phone number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone || ""}
                onChange={(e) => handleFieldChange("phone", e.target.value)}
                placeholder="+1 (555) 000-0000"
                disabled={submitting}
                aria-invalid={!!errors.phone}
                className={
                  errors.phone
                    ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                    : ""
                }
              />
              {errors.phone && (
                <p className="text-sm text-red-600" role="alert">
                  {errors.phone}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ============ STEP 2: Password ============ */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="password">
                Password <span className="text-red-500">*</span>
              </Label>
              <PasswordInput
                id="password"
                value={formData.password || ""}
                onChange={(e) => handleFieldChange("password", e.target.value)}
                placeholder="Create a password"
                disabled={submitting}
                autoComplete="new-password"
                aria-invalid={!!errors.password}
                className={
                  errors.password
                    ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                    : ""
                }
              />
              {errors.password && (
                <p className="text-sm text-red-600" role="alert">
                  {errors.password}
                </p>
              )}

              {passwordValue && (
                <div className="space-y-2 pt-1">
                  <div className="flex gap-1.5">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${i < strength ? strengthColors[strength - 1] : "bg-slate-200"}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    Strength: {strengthLabels[strength - 1] || "Too short"}
                  </p>
                  <div className="space-y-1">
                    {[
                      { met: passwordValue.length >= 8, label: "At least 8 characters" },
                      {
                        met: /[a-z]/.test(passwordValue) && /[A-Z]/.test(passwordValue),
                        label: "Upper & lowercase letters",
                      },
                      { met: /\d/.test(passwordValue), label: "At least one number" },
                      {
                        met: /[^a-zA-Z0-9]/.test(passwordValue),
                        label: "At least one special character",
                      },
                    ].map((req) => (
                      <div key={req.label} className="flex items-center gap-2 text-xs">
                        {req.met ? (
                          <Check className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                          <X className="h-3.5 w-3.5 text-slate-400" />
                        )}
                        <span className={req.met ? "text-green-700" : "text-slate-500"}>
                          {req.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword">
                Confirm password <span className="text-red-500">*</span>
              </Label>
              <PasswordInput
                id="confirmPassword"
                value={formData.confirmPassword || ""}
                onChange={(e) => handleFieldChange("confirmPassword", e.target.value)}
                placeholder="Confirm your password"
                disabled={submitting}
                autoComplete="new-password"
                aria-invalid={!!errors.confirmPassword}
                className={
                  errors.confirmPassword
                    ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                    : ""
                }
              />
              {formData.confirmPassword && formData.password === formData.confirmPassword && (
                <p className="flex items-center gap-1.5 text-xs text-green-600">
                  <Check className="h-3.5 w-3.5" /> Passwords match
                </p>
              )}
              {errors.confirmPassword && (
                <p className="text-sm text-red-600" role="alert">
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ============ STEP 3: Additional info + submit ============ */}
        {step === 3 && (
          <div className="space-y-5">
            {/* Dynamic fields */}
            {config.formFields.length > 0 && (
              <fieldset className="space-y-4">
                <legend className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Additional information
                </legend>
                {config.formFields.map(renderField)}
              </fieldset>
            )}

            {/* Profile picture */}
            {config.requireProfilePicture && (
              <>
                {config.formFields.length > 0 && <div className="border-t border-slate-200" />}
                <div className="space-y-1.5">
                  <Label htmlFor="profilePicture">
                    Profile picture <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-slate-500">
                    Maximum size: {REGISTRATION_PROFILE_PHOTO_MAX_LABEL}.
                  </p>
                  <label
                    htmlFor="profilePicture"
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors hover:bg-slate-50 ${errors.profilePicture ? "border-red-400" : "border-slate-300"}`}
                  >
                    {profilePicture ? (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        {profilePicture.name}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 text-slate-500">
                        <Upload className="h-6 w-6" />
                        <span className="text-sm">Click to upload</span>
                      </div>
                    )}
                  </label>
                  <Input
                    id="profilePicture"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "profilePicture")}
                    className="hidden"
                  />
                  {errors.profilePicture && (
                    <p className="text-sm text-red-600" role="alert">
                      {errors.profilePicture}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Documents */}
            {config.requireDocuments && config.requireDocuments.length > 0 && (
              <>
                <div className="border-t border-slate-200" />
                <fieldset className="space-y-4">
                  <legend className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Required documents
                  </legend>
                  <p className="text-xs text-slate-500">
                    Each file must be {REGISTRATION_DOCUMENT_MAX_LABEL} or smaller.
                  </p>
                  {config.requireDocuments.map((docType, index) => {
                    const documentError = errors[`document:${docType}`];
                    return (
                      <div key={index} className="space-y-1.5">
                        <Label htmlFor={`document-${index}`}>
                          {docType} <span className="text-red-500">*</span>
                        </Label>
                        {documentFiles[docType] ? (
                          <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-200">
                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                            <span className="truncate flex-1">{documentFiles[docType].name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const d = { ...documentFiles };
                                delete d[docType];
                                setDocumentFiles(d);
                                setErrors((prev) => {
                                  if (!prev[`document:${docType}`]) return prev;
                                  const next = { ...prev };
                                  delete next[`document:${docType}`];
                                  return next;
                                });
                              }}
                              className="h-7 w-7 p-0 shrink-0"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <label
                            htmlFor={`document-${index}`}
                            className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 hover:bg-slate-50 ${(errors.documents && !documentFiles[docType]) || documentError ? "border-red-400" : "border-slate-300"}`}
                          >
                            <div className="flex flex-col items-center gap-1 text-slate-500">
                              <Upload className="h-5 w-5" />
                              <span className="text-xs">Upload {docType}</span>
                            </div>
                          </label>
                        )}
                        <Input
                          id={`document-${index}`}
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const sizeError = registrationFileSizeError(file, "document");
                            if (sizeError) {
                              e.target.value = "";
                              setErrors((prev) => ({
                                ...prev,
                                [`document:${docType}`]: sizeError,
                              }));
                              return;
                            }
                            setDocumentFiles({ ...documentFiles, [docType]: file });
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next[`document:${docType}`];
                              delete next.documents;
                              return next;
                            });
                          }}
                          className="hidden"
                        />
                        {documentError && (
                          <p className="text-sm text-red-600" role="alert">
                            {documentError}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {errors.documents && (
                    <p className="text-sm text-red-600" role="alert">
                      {errors.documents}
                    </p>
                  )}
                </fieldset>
              </>
            )}

            {/* Branch preference */}
            {config.showBranchPreference && branches.length > 0 && (
              <>
                <div className="border-t border-slate-200" />
                <div className="space-y-1.5">
                  <Label htmlFor="homeBranch">Preferred branch</Label>
                  <Select value={selectedHomeBranch} onValueChange={setSelectedHomeBranch}>
                    <SelectTrigger id="homeBranch">
                      <SelectValue placeholder="Select your preferred branch (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                          {b.isMain ? " (Main Library)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Your home branch determines your default service location. You can change this
                    later.
                  </p>
                </div>
              </>
            )}

            {/* Show "nothing extra needed" if step has no content */}
            {!hasAdditionalStep && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm text-primary text-left">
                    No additional information is required. You can submit your registration.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {submitError && (
          <p className="text-sm text-red-600" role="alert">
            {submitError}
          </p>
        )}

        {/* ============ Navigation buttons ============ */}
        <div className="flex gap-3 pt-2">
          {step > 0 && (
            <Button type="button" variant="outline" onClick={goBack} className="shrink-0">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          {isLastStep ? (
            <Button
              type="button"
              className="flex-1"
              size="lg"
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create account
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button type="button" className="flex-1" size="lg" onClick={goNext}>
              Continue
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Footer link */}
        <p className="text-center text-sm text-slate-500">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-primary font-medium hover:text-primary-600 transition-colors"
          >
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;
